import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, X, Plus, Trash2, CheckCircle2, Check } from "lucide-react";
import { api } from "../lib/api";
import { Overline } from "./Layout";
import { getUtmForRegistration } from "../lib/utm";

const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const httpsish = (v) => !v || /^https?:\/\//i.test(v) || /\./.test(v);

// Interests options are organizer-managed (served from /config/interests). Cached
// module-wide so the picker doesn't refetch on every open.
let _interestsCache = null;
function useInterests() {
  const [opts, setOpts] = useState(_interestsCache || []);
  useEffect(() => {
    if (_interestsCache) return;
    api.get("/config/interests")
      .then((r) => { _interestsCache = r.data.interests || []; setOpts(_interestsCache); })
      .catch(() => {});
  }, []);
  return opts;
}

// MUST stay at module scope. Defined inside RegistrationForm, this got a new
// function identity on every render, so React treated it as a different component
// type, unmounted the subtree and remounted a fresh <input> — the field lost focus
// after every single keystroke.
function Field({ label, required, children }) {
  return (
    <div>
      <div className="overline mb-1">
        {label}{required && <span style={{ color: "hsl(var(--vd-coral))" }}> *</span>}
      </div>
      {children}
    </div>
  );
}

// The attendee profile the product exists to build (FR-REG-5). One component for
// both the full first-time form and the compact returning/confirm view — the
// backend tells us which via verified.next ("profile_form" | "confirm").
//
// Branch semantics:
//   next === "confirm"       -> returning attendee or member; prefill known, show
//                               a compact confirmation (name + edit affordance).
//   otherwise (profile_form) -> first-time non-member; collect the full profile
//                               with the required fields enforced.
export default function RegistrationForm({ ev, verified, ctaLabel, onCancel, onDone }) {
  const prefill = verified?.prefill || {};
  const isConfirm = verified?.next === "confirm";
  const interestOptions = useInterests();

  const splitName = (prefill.name || "").trim();
  const [form, setForm] = useState({
    first_name: prefill.first_name || splitName.split(" ")[0] || "",
    last_name: prefill.last_name || splitName.split(" ").slice(1).join(" ") || "",
    email: verified?.email || prefill.email || "",
    company: prefill.company || "",
    company_website: prefill.website || "",
    linkedin: prefill.linkedin || "",
    is_founder: prefill.is_founder ?? null,
    building: prefill.building || prefill.working_on || "",
    interests: Array.isArray(prefill.interests) ? prefill.interests : [],
    answers: {},
  });
  // A returning attendee lands on a compact confirm, but can expand to edit.
  const [expanded, setExpanded] = useState(!isConfirm);
  const [guests, setGuests] = useState([]);
  const [customInterest, setCustomInterest] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleInterest = (opt) =>
    set({ interests: form.interests.includes(opt)
      ? form.interests.filter((i) => i !== opt)
      : [...form.interests, opt] });
  const addCustomInterest = () => {
    const v = customInterest.trim();
    if (v && !form.interests.includes(v)) set({ interests: [...form.interests, v] });
    setCustomInterest("");
  };

  const addGuest = () => setGuests([...guests, { id: crypto.randomUUID(), name: "" }]);
  const removeGuest = (id) => setGuests(guests.filter((g) => g.id !== id));
  const updateGuest = (id, name) => setGuests(guests.map((g) => g.id === id ? { ...g, name } : g));

  const requiredMsg = () => {
    if (!form.first_name.trim()) return "First name is required";
    if (!form.last_name.trim()) return "Last name is required";
    if (!form.email.trim() || !emailValid(form.email)) return "A valid email is required";
    // The full profile is required only for a first-time attendee. A returning
    // one confirming has already given it; don't force a re-entry.
    if (!isConfirm) {
      if (!form.company.trim()) return "Company is required";
      if (form.is_founder === null) return "Please tell us if you're a founder";
    }
    if (!httpsish(form.company_website)) return "Company website looks off";
    return null;
  };

  const submit = async () => {
    const err = requiredMsg();
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const r = await api.post(`/events/${ev.id}/register`, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: verified?.email || form.email.trim(),
        company: form.company.trim() || null,
        company_website: form.company_website.trim() || null,
        linkedin: form.linkedin.trim() || null,
        is_founder: form.is_founder,
        building: form.building.trim() || null,
        interests: form.interests,
        guests: guests.map((g) => g.name).filter(Boolean),
        answers: form.answers,
        registration_token: verified?.token,
        ...getUtmForRegistration(),
      });
      onDone({ status: r.data.status || "going", qr: r.data.qr_code });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  const label = busy ? "Submitting…" : (ctaLabel || (ev.require_approval ? "Request to join" : "Register"));
  const btnClass = ev.require_approval ? "btn-peach" : "btn-primary";

  // Compact confirmation for a returning attendee / member.
  if (isConfirm && !expanded) {
    return (
      <div className="mt-2" data-testid="register-confirm">
        <div className="flex items-center justify-between">
          <Overline>You're recognized</Overline>
          <button onClick={onCancel} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-white/60" data-testid="register-cancel"><X size={16} /></button>
        </div>
        <div className="mt-3 card-cream px-4 py-3 rounded-xl">
          <div className="font-heading text-lg leading-tight">
            {[form.first_name, form.last_name].filter(Boolean).join(" ") || form.email}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 font-mono">{form.email}</div>
          {form.company && <div className="text-sm mt-1">{form.company}</div>}
        </div>
        <button onClick={() => setExpanded(true)} className="text-xs mt-2 hover:underline" style={{ color: "hsl(var(--vd-green))" }} data-testid="register-edit-details">
          Update my details
        </button>
        <button onClick={submit} disabled={busy} className={`w-full mt-4 ${btnClass}`} data-testid="modal-submit">
          {label} <ArrowRight size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2" data-testid="register-form">
      <div className="flex items-center justify-between">
        <div>
          <Overline>{isConfirm ? "Your details" : "Tell us about you"}</Overline>
          <div className="font-heading text-lg mt-0.5 leading-tight">{ev.title}</div>
        </div>
        <button onClick={onCancel} className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-white/60" data-testid="register-cancel"><X size={16} /></button>
      </div>

      <div className="mt-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" required>
            <input className="input-field" value={form.first_name} onChange={(e) => set({ first_name: e.target.value })} data-testid="modal-first-name" />
          </Field>
          <Field label="Last name" required>
            <input className="input-field" value={form.last_name} onChange={(e) => set({ last_name: e.target.value })} data-testid="modal-last-name" />
          </Field>
        </div>

        <Field label="Email">
          <div className="relative">
            <input type="email" className="input-field" value={form.email}
              onChange={(e) => set({ email: e.target.value })}
              readOnly={!!verified} data-testid="modal-email" />
            {!!verified && <CheckCircle2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "hsl(var(--vd-green))" }} />}
          </div>
        </Field>

        <Field label="Company" required={!isConfirm}>
          <input className="input-field" placeholder="Where you work, or your startup" value={form.company} onChange={(e) => set({ company: e.target.value })} data-testid="modal-company" />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Company website">
            <input className="input-field" placeholder="https://" value={form.company_website} onChange={(e) => set({ company_website: e.target.value })} data-testid="modal-website" />
          </Field>
          <Field label="LinkedIn">
            <input className="input-field" placeholder="linkedin.com/in/…" value={form.linkedin} onChange={(e) => set({ linkedin: e.target.value })} data-testid="modal-linkedin" />
          </Field>
        </div>

        <Field label="Are you a founder?" required={!isConfirm}>
          <div className="flex gap-2" data-testid="modal-founder">
            {[["Yes", true], ["No", false]].map(([lbl, val]) => (
              <button key={lbl} type="button" onClick={() => set({ is_founder: val })}
                className={`flex-1 h-9 rounded-lg text-sm border transition ${form.is_founder === val ? "text-white" : "hover:bg-white/60"}`}
                style={form.is_founder === val
                  ? { background: "hsl(var(--vd-green))", borderColor: "hsl(var(--vd-green))" }
                  : { borderColor: "hsl(var(--border))" }}
                data-testid={`modal-founder-${String(val)}`}>
                {lbl}
              </button>
            ))}
          </div>
        </Field>

        <Field label="What are you building?">
          <input className="input-field" placeholder="One-liner is fine" value={form.building} onChange={(e) => set({ building: e.target.value })} data-testid="modal-building" />
        </Field>

        <Field label="Interests">
          <div className="flex flex-wrap gap-1.5" data-testid="modal-interests">
            {interestOptions.map((opt) => {
              const on = form.interests.includes(opt);
              return (
                <button key={opt} type="button" onClick={() => toggleInterest(opt)}
                  className={`chip text-xs transition ${on ? "text-white" : "hover:bg-white/60"}`}
                  style={on ? { background: "hsl(var(--vd-purple))", borderColor: "hsl(var(--vd-purple))" } : {}}
                  data-testid={`interest-${opt}`}>
                  {on && <Check size={11} className="inline mr-1" />}{opt}
                </button>
              );
            })}
          </div>
          {/* Free-text "other" so the managed list never has to be exhaustive. */}
          <div className="flex gap-2 mt-2">
            <input className="input-field" placeholder="Add your own…" value={customInterest}
              onChange={(e) => setCustomInterest(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomInterest(); } }}
              data-testid="modal-interest-custom" />
            <button type="button" onClick={addCustomInterest} className="h-9 px-3 rounded-lg border hover:bg-white/60 text-sm" style={{ borderColor: "hsl(var(--border))" }}><Plus size={13} /></button>
          </div>
          {form.interests.filter((i) => !interestOptions.includes(i)).map((i) => (
            <span key={i} className="chip chip-purple text-xs mt-1.5 mr-1.5 inline-flex items-center gap-1">
              {i}<button type="button" onClick={() => toggleInterest(i)}><X size={10} /></button>
            </span>
          ))}
        </Field>

        {ev.group_registration && (
          <Field label={<span className="flex items-center justify-between"><span>Guests</span>
            <button onClick={addGuest} className="text-xs flex items-center gap-1 rounded-lg px-2 py-1 hover:bg-white/60" data-testid="modal-add-guest"><Plus size={11} />Add guest</button></span>}>
            <div className="space-y-2">
              {guests.length === 0 && <div className="text-xs text-muted-foreground">No guests. Click Add guest to include a +1.</div>}
              {guests.map((g, i) => (
                <div key={g.id} className="flex gap-2 items-center" data-testid={`modal-guest-${i}`}>
                  <input className="input-field" placeholder={`Guest ${i + 1} name`} value={g.name} onChange={(e) => updateGuest(g.id, e.target.value)} />
                  <button onClick={() => removeGuest(g.id)} className="h-9 w-9 flex items-center justify-center rounded-lg hover:bg-white/60" style={{ color: "hsl(var(--vd-coral))" }}><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          </Field>
        )}

        {(ev.custom_questions || []).map((q) => (
          <Field key={q.id} label={q.label} required={q.required}>
            {q.type === "select" ? (
              <select className="input-field" value={form.answers[q.id] || ""} onChange={(e) => set({ answers: { ...form.answers, [q.id]: e.target.value } })}>
                <option value="">Select…</option>
                {q.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : q.type === "checkbox" ? (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!form.answers[q.id]} onChange={(e) => set({ answers: { ...form.answers, [q.id]: e.target.checked ? "yes" : "" } })} className="accent-primary" />
                <span>Yes</span>
              </label>
            ) : (
              <input className="input-field" value={form.answers[q.id] || ""} onChange={(e) => set({ answers: { ...form.answers, [q.id]: e.target.value } })} />
            )}
          </Field>
        ))}
      </div>

      <button onClick={submit} disabled={busy} className={`w-full mt-6 ${btnClass}`} data-testid="modal-submit">
        {label} <ArrowRight size={14} />
      </button>
    </div>
  );
}
